import { useLanguage } from "@/lib/i18n";
import { getFixedPriceTotal, formatFixedPrice, FREE_SHIPPING_THRESHOLDS } from "@/lib/fixedPrices";
import { Progress } from "@/components/ui/progress";
import { Truck } from "lucide-react";

interface FreeShippingBarProps {
  items: Array<{ handle: string; quantity: number }>;
}

export function FreeShippingBar({ items }: FreeShippingBarProps) {
  const { t, currency } = useLanguage();

  const displayCurrency = currency || 'USD';

  // CAD uses Canada Post calculated rates — no fixed free-shipping threshold
  if (displayCurrency === 'CAD') return null;

  const threshold = FREE_SHIPPING_THRESHOLDS[displayCurrency] || FREE_SHIPPING_THRESHOLDS.USD;

  // Sum fixed prices — matches exactly what the customer sees per item
  const totalInDisplayCurrency = getFixedPriceTotal(items, displayCurrency);

  const progress = Math.min((totalInDisplayCurrency / threshold) * 100, 100);
  const remaining = Math.max(threshold - totalInDisplayCurrency, 0);
  const isUnlocked = remaining <= 0;
  const remainingCeiled = Math.ceil(remaining);

  const formattedRemaining = formatFixedPrice(remainingCeiled, displayCurrency);

  return (
    <div className="py-3">
      <div className="flex items-center gap-2 mb-2">
        <Truck className={`w-4 h-4 flex-shrink-0 ${isUnlocked ? 'text-emerald-600' : 'text-primary'}`} />
        <span className={`text-xs font-medium ${isUnlocked ? 'text-emerald-700' : 'text-foreground'}`}>
          {isUnlocked
            ? t('cart.freeShippingUnlocked')
            : t('cart.freeShippingRemaining', { amount: formattedRemaining })
          }
        </span>
      </div>
      <Progress
        value={progress}
        className={`h-2 ${isUnlocked ? '[&>div]:bg-emerald-500' : ''}`}
      />
    </div>
  );
}
